import {
  Button,
  ButtonIcon,
  Heading,
  isSerialNumberValid,
  LoadingSpinner,
  MenuListSelect,
} from '@rabbit/elements/shared-components';
import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { CreateNewConsumerForm_FormValues } from '@rabbit/sage/components/organisms/ModalNewRegistration/tenant-config/types.ts';
import {
  FileStorageContext,
  getCompletedUploadsOfCategory,
} from '@rabbit/bizproc/react';
import {
  DTVendable,
  FBD_Consumer_Private,
  FBD_Holding_Private,
  FBD_HoldingProxy,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import {
  CompletedUploadShape,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import { Card, CardMedia, SelectChangeEvent, Typography } from '@mui/material';
import { Error, ExpandMore } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useQueryParams } from 'raviger';
import { SearchVendablesBySkuCode } from '@rabbit/search/cherchons';
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import { AppContext } from '@rabbit/app-context';
import PartnerDetailsView from '@rabbit/sage/views/PartnerDetailsView.tsx';
import CustomerDetailsView from '@rabbit/sage/views/CustomerDetailsView.tsx';
import ProductDetailsView from '@rabbit/sage/views/ProductDetailsView.tsx';
import { AVAILABLE_LANGUAGE_CODES } from '@rabbit/sage/utils/consts.ts';
import { nestApiRegisterInstallerHoldings } from '@rabbit/bizproc/core';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  padding: '0px',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(90deg)',
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '0px',
}));

export default function RegisterProductInstaller() {
  const { t, i18n } = useTranslation();
  const params = useQueryParams();
  const steps = ['installer-create', 'customer-create', 'register-product'];
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const [customerData, setCustomerData] =
    useState<CreateNewConsumerForm_FormValues>();
  const [productData, setProductData] = useState<any>();
  const [installerData, setInstallerData] = useState<any>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registered, setRegistered] = useState<boolean>(false);
  const [defaultSelected, setDefaultSelected] = useState<any>(undefined);
  const [productLoaded, setProductLoaded] = useState<boolean>(false);
  const [vendable, setVendable] = useState<DTVendable | null>(null);
  const [errorSubmit, setErrorSubmit] = useState<boolean>(false);
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);
  // Ref to track if the serial number already used error has been shown
  const serialNumberErrorShown = useRef(false);

  const { config } = useContext(AppContext);
  const [isValidSN, setIsValidSN] = useState<boolean>(true);

  const { uploadQueueState } = useContext(FileStorageContext) || {};
  const { completed } = uploadQueueState ?? {};

  const installerAttachmentUploads: CompletedUploadShape[] =
    getCompletedUploadsOfCategory(
      completed ?? [],
      UploadedFileCategories.installation_attachment
    );

  const proofAttachmentUploads: CompletedUploadShape[] =
    getCompletedUploadsOfCategory(
      completed ?? [],
      UploadedFileCategories.ConsumerProofPurchase
    );

  const installationAttachmentFiles = installerAttachmentUploads?.flatMap(
    (file) =>
      file.category === UploadedFileCategories.installation_attachment &&
      file?.uploadedFile
        ? [file.uploadedFile]
        : []
  );
  const proofAttachmentFiles = proofAttachmentUploads?.flatMap((file) =>
    file.category === UploadedFileCategories.ConsumerProofPurchase &&
    file?.uploadedFile
      ? [file.uploadedFile]
      : []
  );

  const onSubmitProductData = async (values: any) => {
    setProductData(values);
    setIsSubmitting(true);

    // Check if serial number validation is required and if the serial number is already used
    if (
      config?.HOLDINGS?.SERIAL_NUMBER_VALIDATION?.ENABLED &&
      vendable?.serialNumberEnabled
    ) {
      const serialNumber = values.serial;

      if (serialNumber) {
        try {
          const mfr = `${
            PersonaTypeSingleLetter.Manufacturer
          }${PersonaIdTypeSplitter}${t('tenantLink')}`;
          // Check if the serial number is already used in any holding proxy
          const query = FBD_HoldingProxy.query()
            .where('mfr', '==', mfr)
            .where('serial', '==', serialNumber);
          const existingHoldings = await query.getContents();

          if (existingHoldings && existingHoldings.length > 0) {
            if (!serialNumberErrorShown.current) {
              toast.error(t('general.serialNumberAlreadyUsed'));
              serialNumberErrorShown.current = true;
            }
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          console.error('Error checking serial number:', error);
          toast.error(t('general.somethingWentWrongPleaseTryAgain'));
          setIsSubmitting(false);
          return;
        }
      }
    }

    const newPurchaseLocation =
      values.custom_store_name && values.store_not_listed
        ? null
        : values.purchase_location?.docid ?? values.purchase_location ?? '';
    const newPurchaseLocationOther = values.store_not_listed
      ? values.custom_store_name ?? ''
      : null;
    const newPurchaseCountry =
      values.custom_store_name && values.store_not_listed
        ? values.purchase_country
        : values.purchase_location?.country ?? values.purchase_country ?? '';
    const finalData = {
      ...installerData,
      ...customerData,
      ...values,
      consumer_holding_purchase_location: newPurchaseLocation,
      consumer_holding_purchase_location_other: newPurchaseLocationOther,
      consumer_holding_purchase_country: newPurchaseCountry,
    };
    finalData.installation_attachment = installationAttachmentFiles;
    finalData.consumer_proof_of_purchase = proofAttachmentFiles;
    try {
      const response = await nestApiRegisterInstallerHoldings({
        formData: finalData,
        tenantLink: t('tenantLink'),
        newHoldingLink: holding_docid,
        newConsumerLink: newConsumerLink,
      });
      if (response) {
        setIsSubmitting(false);
        setRegistered(true);
      } else {
        setIsSubmitting(false);
        setErrorSubmit(true);
      }
    } catch (error) {
      setErrorSubmit(true);
    }
  };
  useEffect(() => {
    const loadProduct = async () => {
      const sku = params[0]?.sku || '';

      if (!sku || productLoaded) {
        setProductLoaded(true);
        return;
      }

      setIsSubmitting(true);

      try {
        const vendableData = await SearchVendablesBySkuCode(sku);
        const firstItem = vendableData?.items[0];

        if (firstItem) {
          setVendable(firstItem);
          setDefaultSelected({
            id: firstItem.docid,
            label: firstItem.title,
            value: firstItem.docid,
          });

          if (
            config?.HOLDINGS?.SERIAL_NUMBER_VALIDATION?.ENABLED &&
            firstItem.serialNumberEnabled
          ) {
            setSerialNumbers(firstItem.serialNumbers || []);
            setSerialNumberRanges(firstItem.serialNumberRanges || []);

            if (params[0]?.sn) {
              // First check if the serial number is valid
              const isValid = isSerialNumberValid(
                params[0].sn,
                firstItem.serialNumbers || [],
                firstItem.serialNumberRanges || []
              );

              if (isValid) {
                // If the serial number is valid, check if it's already used in any holding proxy
                try {
                  const mfr = `${
                    PersonaTypeSingleLetter.Manufacturer
                  }${PersonaIdTypeSplitter}${t('tenantLink')}`;

                  const query = FBD_HoldingProxy.query()
                    .where('mfr', '==', mfr)
                    .where('serial', '==', params[0].sn);

                  const existingHoldings = await query.getContents();

                  // Set isValidSN to true only if the serial number is valid AND not already used
                  setIsValidSN(!(existingHoldings && existingHoldings.length > 0));

                  if (existingHoldings && existingHoldings.length > 0) {
                    if (!serialNumberErrorShown.current) {
                      toast.error(t('general.serialNumberAlreadyUsed'));
                      serialNumberErrorShown.current = true;
                    }
                  }
                } catch (error) {
                  console.error('Error checking serial number in holding proxy:', error);
                  // If there's an error checking the holding proxy, assume the serial number is valid
                  // since we already checked it with isSerialNumberValid
                  setIsValidSN(true);
                }
              } else {
                // If the serial number is not valid, set isValidSN to false
                setIsValidSN(false);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error loading product:', err);
      } finally {
        setProductLoaded(true);
        setIsSubmitting(false);
      }
    };

    loadProduct();
  }, [
    params,
    productLoaded,
    config?.HOLDINGS?.SERIAL_NUMBER_VALIDATION?.ENABLED,
  ]);

  const holding_private = FBD_Holding_Private.empty();
  const holding_docid = FBD_Holding_Private.keygen(
    holding_private,
    FBD_Holding_Private
  );

  const consumer_private = FBD_Consumer_Private.empty();
  const newConsumerLink = FBD_Consumer_Private.keygen(
    consumer_private,
    FBD_Consumer_Private
  );

  const [lang, setLang] = useState(window.localStorage.getItem('lang') ?? 'en');

  const handleChange = async (event: SelectChangeEvent) => {
    setLang(event.target.value as string);
    window.localStorage.setItem('lang', event.target.value as string);
    await i18n.changeLanguage(event.target.value as string);
  };
  const availableLanguages = AVAILABLE_LANGUAGE_CODES.map((data) => ({
    value: data.code,
    label: config.ACCOUNT.LANGUAGE_LABEL ? data.language : data.code,
  }));
  const cobrand = t('CFG_COBRAND_LOGO');
  const productPlaceHolder = t(
    'CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER',
    { defaultValue: '' }
  );
  const defaultImage =
    t('CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER') || productPlaceHolder;

  return (
    <>
      {(registered || errorSubmit || !isValidSN) && (
        <div className="xs:w-full relative flex flex-col rounded-md bg-white md:m-auto md:w-[480px] md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)] lg:content-center">
          <div className="pl-6 pt-10 pb-4 pr-6">
            {!isValidSN && (
              <div className="mb-6 w-full rounded-lg border border-red-200 bg-red-50 py-8 text-center">
                <div className="flex flex-col items-center">
                  <Error className="mb-4 h-12 w-12 text-red-500" />
                  <p className="text-xl font-medium text-gray-800">
                    {t('message.serialNumberNotFound')}
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-gray-600">
                    {t('general.pleaseCheckSerialNumberAndTryAgain')}
                  </p>
                </div>
              </div>
            )}
            {errorSubmit && (
              <div className="mb-6 w-full rounded-lg border border-red-200 bg-red-50 py-8 text-center">
                <div className="flex flex-col items-center">
                  <Error className="mb-4 h-12 w-12 text-red-500" />
                  <p className="text-xl font-medium text-gray-800">
                    {t('message.productRegisterFailed')}
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-gray-600">
                    {t('general.somethingWentWrongPleaseTryAgain')}
                  </p>
                  <div className="mt-6 flex items-center justify-center">
                    <span className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium transition-colors duration-200">
                      {t('general.contactSupport')}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {registered && (
              <div className="mb-6 w-full rounded-lg border border-green-200 bg-green-50 py-4 text-center">
                <div className="flex flex-col items-center">
                  <img
                    alt=""
                    src={cobrand}
                    className="mb-4 max-h-[72px] w-full object-contain"
                  />

                  {vendable && (
                    <>
                      <div className="mb-4 flex w-full items-center justify-center">
                        <img
                          alt=""
                          src={
                            vendable.img && vendable.img.length > 0
                              ? vendable.img[0]
                              : defaultImage
                          }
                          className="h-[200px] object-contain"
                        />
                      </div>

                      <div className="font-nunito mb-3 text-center text-xl font-medium">
                        {vendable.mpn}
                      </div>
                      <div className="font-nunito mb-6 text-center text-lg font-medium">
                        {vendable.title}
                      </div>

                      <div className="mx-auto flex w-full max-w-md items-center justify-center gap-3 rounded-lg bg-white py-4 text-xl shadow-sm">
                        <ShieldCheckIcon className="h-8 w-8 text-green-600" />
                        <span className="font-medium text-green-700">
                          {t('general.productRegisteredSuccess')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {isSubmitting && (
        <div className="xs:w-full relative flex flex-col rounded-md bg-white md:m-auto md:w-[480px] md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)] lg:content-center">
          <div className="pl-6 pt-4 pb-4">
            <LoadingSpinner size={'lg'} />
          </div>
        </div>
      )}
      {!isSubmitting && !registered && isValidSN && (
        <div className="xs:w-full relative flex flex-col rounded-md bg-white md:m-auto md:w-[480px] md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)] lg:content-center">
          {/*<div style={{ position: 'absolute', right: 10, top: 10 }}>
            <MenuListSelect
              label="lang"
              value={lang}
              handleChange={handleChange}
              menuItem={availableLanguages}
            />
          </div>*/}
          <div className="flex flex-col gap-2 border-b border-gray-100 p-10 pb-6 md:flex-row">
            <div className="flex max-h-[100px] w-full">
              <img
                src={vendable && vendable.img ? vendable?.img[0] : defaultImage}
                className="w-[100px] object-contain"
                alt=""
              />
              <div className="ml-2 w-full md:ml-2">
                <div className="font-nunito mt-0 pt-0 text-xl font-semibold">
                  {t('general.productRegistration')}
                </div>
                <div className="font-nunito text-sm text-gray-600">
                  {vendable && vendable.title ? vendable.title : ''}
                </div>
              </div>
            </div>
          </div>
          <div className="p-10 pt-6">
            <Accordion
              hidden={currentStep !== steps[0]}
              expanded={currentStep === steps[0]}
            >
              <div className="pb-6">
                <Heading kind="h3">{t('general.installerDetails')}</Heading>
              </div>
              <AccordionDetails>
                <PartnerDetailsView
                  steps={steps}
                  setInstallerData={setInstallerData}
                  setCurrentStep={setCurrentStep}
                />
              </AccordionDetails>
            </Accordion>

            <Accordion
              hidden={currentStep !== steps[1]}
              expanded={currentStep === steps[1]}
            >
              <div className="flex gap-4 pb-6">
                <Heading kind="h3">
                  {t('general.customerDetails')}

                  <div className="font-nunito text-sm text-gray-500">
                    {' '}
                    ({t('general.houseOwner')})
                  </div>
                </Heading>
              </div>
              <AccordionDetails>
                <CustomerDetailsView
                  steps={steps}
                  setCustomerData={setCustomerData}
                  setCurrentStep={setCurrentStep}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              hidden={currentStep !== steps[2]}
              expanded={currentStep === steps[2]}
            >
              <div className="pb-6">
                <Heading kind="h3">{t('general.productDetails')}</Heading>
              </div>
              <AccordionDetails>
                <ProductDetailsView
                  steps={steps}
                  setCurrentStep={setCurrentStep}
                  defaultSelected={defaultSelected}
                  onSubmitProductData={onSubmitProductData}
                  holding_docid={holding_docid}
                  newConsumerLink={newConsumerLink}
                  setVendable={setVendable}
                />
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      )}
    </>
  );
}
